import React from 'react';

/**
 * Componente unificado e reutilizável de Abas de Navegação (TabsNav)
 * Usado tanto nas Configurações quanto no detalhamento de Faturas de Cartão.
 *
 * @param {Array<{ id: string, label: string, icon?: React.ComponentType, badge?: string, count?: number, disabled?: boolean }>} tabs
 * @param {string} active
 * @param {(id: string) => void} onChange
 * @param {string} className
 * @param {string} accentColor
 */
export default function TabsNav({
  tabs = [],
  active,
  onChange,
  className = '',
  accentColor = '#6366F1',
}) {
  return (
    <div
      className={`flex items-center gap-1.5 p-1 bg-slate-100/90 dark:bg-slate-900/80 rounded-2xl border border-slate-200/70 dark:border-slate-800 overflow-x-auto no-scrollbar scroll-smooth ${className}`}
      role="tablist"
    >
      {tabs.map((tab) => {
        const isActive = active === tab.id;
        const Icon = tab.icon;

        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={tab.disabled}
            onClick={() => !tab.disabled && onChange(tab.id)}
            data-testid={`tab-${tab.id}`}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer select-none ${
              isActive
                ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 shadow-sm border border-slate-200/60 dark:border-slate-700/80'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-white/50 dark:hover:bg-slate-800/40'
            } ${tab.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={isActive ? { color: accentColor } : {}}
          >
            {Icon && (
              <Icon
                size={16}
                className="flex-shrink-0 transition-transform duration-200"
                style={isActive ? { color: accentColor } : {}}
              />
            )}
            <span>{tab.label}</span>

            {tab.count != null && (
              <span
                className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  isActive
                    ? 'bg-indigo-50 dark:bg-indigo-950/70 text-indigo-600 dark:text-indigo-400'
                    : 'bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                }`}
              >
                {tab.count}
              </span>
            )}

            {tab.badge && (
              <span className="ml-1 px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider bg-slate-200/80 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-300/40 dark:border-slate-700">
                {tab.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
